// New Envelope Wizard — upload PDF → add signers → EZSign-style drag-drop field placement → send.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  ArrowLeft, ArrowRight, Upload, UserPlus, X, Send, PenTool,
  Type as TypeIcon, Calendar, CheckSquare, Search, Trash2, Copy, GripHorizontal,
} from "lucide-react";
import api from "./api";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const FIELD_KINDS = [
  { type: "signature", label: "Signature", icon: PenTool, w: 0.22, h: 0.045 },
  { type: "initial",   label: "Initial",   icon: PenTool, w: 0.08, h: 0.04 },
  { type: "date",      label: "Date",      icon: Calendar, w: 0.14, h: 0.03 },
  { type: "text",      label: "Text",      icon: TypeIcon, w: 0.18, h: 0.03 },
  { type: "checkbox",  label: "Checkbox",  icon: CheckSquare, w: 0.03, h: 0.03 },
];

const SIGNER_COLORS = ["#0071e3", "#af52de", "#ff9f0a", "#30d158", "#ff453a", "#5ac8fa", "#ffcc00", "#ff2d55"];

export default function SignNew() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1=upload+setup 2=signers 3=fields 4=review
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [pdfB64, setPdfB64] = useState("");   // base64 for backend
  const [pdfSrc, setPdfSrc] = useState(null); // data URL for react-pdf
  const [pdfName, setPdfName] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [signers, setSigners] = useState([]); // {tempId, name, email, role, contact_id, color}
  const [fields, setFields] = useState([]);   // {tempId, signer_tempId, page, x_ratio, y_ratio, w_ratio, h_ratio, type}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { setError("PDF only"); return; }
    if (f.size > 8 * 1024 * 1024) { setError("Max 8 MB for MVP"); return; }
    setError("");
    setPdfName(f.name);
    const buf = await f.arrayBuffer();
    const b64 = arrayBufferToB64(buf);
    setPdfB64(b64);
    setPdfSrc(`data:application/pdf;base64,${b64}`);
  };

  const canNext =
    (step === 1 && subject.trim() && pdfB64) ||
    (step === 2 && signers.length > 0 && signers.every((s) => s.name && s.email)) ||
    (step === 3 && fields.length > 0);

  const create = async () => {
    setBusy(true); setError("");
    try {
      // 1. Create envelope
      const { data } = await api.post("/sign/envelopes", {
        subject, message, doc_b64: pdfB64, doc_name: pdfName, doc_pages: numPages,
      });
      const envelopeId = data.id;
      // 2. Signers (server generates tokens + ids)
      const signersPayload = signers.map((s, i) => ({
        name: s.name, email: s.email, role: s.role || "client", contact_id: s.contact_id || null, order_index: i,
      }));
      const sigRes = await api.post(`/sign/envelopes/${envelopeId}/signers`, { signers: signersPayload });
      const idMap = {};
      sigRes.data.signers.forEach((s, i) => { idMap[signers[i].tempId] = s.id; });
      // 3. Fields
      const fieldsPayload = fields.map((f) => ({
        signer_id: idMap[f.signer_tempId],
        page: f.page, x_ratio: f.x_ratio, y_ratio: f.y_ratio, w_ratio: f.w_ratio, h_ratio: f.h_ratio,
        type: f.type, required: true,
      })).filter((f) => f.signer_id);
      await api.post(`/sign/envelopes/${envelopeId}/fields`, { fields: fieldsPayload });
      // 4. Send
      await api.post(`/sign/envelopes/${envelopeId}/send`);
      nav(`/crm/sign/${envelopeId}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create envelope");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="sign-new-page" className="space-y-6 pb-24">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <button onClick={() => nav("/crm/sign")} className="inline-flex items-center gap-1 text-[13px] mb-2"
            style={{ color: "var(--crm-text-muted)" }} data-testid="back-to-sign">
            <ArrowLeft size={14} /> Back to envelopes
          </button>
          <h1 className="text-[28px] tracking-tight leading-none"
            style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.02em" }}>
            New envelope
          </h1>
        </div>
        <Stepper step={step} setStep={setStep} />
      </header>

      {error && (
        <div className="text-[13px] rounded-xl px-4 py-2.5"
          style={{ background: "rgba(255,69,58,0.08)", border: "1px solid rgba(255,69,58,0.2)", color: "#ff453a" }}>
          {error}
        </div>
      )}

      {step === 1 && (
        <StepUpload
          subject={subject} setSubject={setSubject}
          message={message} setMessage={setMessage}
          pdfName={pdfName} pdfSrc={pdfSrc}
          onFileChange={onFileChange}
        />
      )}
      {step === 2 && (
        <StepSigners signers={signers} setSigners={setSigners} />
      )}
      {step === 3 && (
        <StepFields
          pdfSrc={pdfSrc} numPages={numPages} setNumPages={setNumPages}
          signers={signers} fields={fields} setFields={setFields}
        />
      )}
      {step === 4 && (
        <StepReview
          subject={subject} pdfName={pdfName} signers={signers} fields={fields}
        />
      )}

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-60 right-0 flex justify-between items-center gap-3 px-8 py-4"
        style={{ background: "var(--crm-topbar-bg)", backdropFilter: "blur(20px)", borderTop: "1px solid var(--crm-line)" }}>
        <button
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
          className="px-4 py-2 rounded-lg text-[13px] disabled:opacity-40"
          style={{ border: "1px solid var(--crm-line)", color: "var(--crm-text)" }}
          data-testid="wizard-back">
          Back
        </button>
        <div className="flex-1" />
        {step < 4 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canNext}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-[13px] font-medium text-black disabled:opacity-40"
            style={{ background: "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)" }}
            data-testid="wizard-next">
            Continue <ArrowRight size={14} />
          </button>
        ) : (
          <button
            onClick={create} disabled={busy}
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-[13px] font-medium text-black disabled:opacity-40"
            style={{ background: "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)" }}
            data-testid="wizard-send">
            <Send size={14} /> {busy ? "Sending…" : "Send envelope"}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Stepper({ step, setStep }) {
  const items = [
    { n: 1, label: "Upload" },
    { n: 2, label: "Signers" },
    { n: 3, label: "Fields" },
    { n: 4, label: "Review" },
  ];
  return (
    <ol className="flex items-center gap-2 text-[12.5px]" data-testid="wizard-stepper">
      {items.map((it, i) => (
        <React.Fragment key={it.n}>
          <li>
            <button
              onClick={() => step >= it.n && setStep(it.n)}
              disabled={step < it.n}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full transition"
              style={{
                background: step === it.n ? "var(--crm-accent-soft)" : "transparent",
                color: step >= it.n ? "var(--crm-text)" : "var(--crm-text-subtle)",
                border: "1px solid var(--crm-line)",
                fontWeight: step === it.n ? 600 : 400,
              }}
              data-testid={`step-${it.n}`}
            >
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px]"
                style={{ background: step >= it.n ? "var(--crm-accent)" : "var(--crm-line)", color: step >= it.n ? "#fff" : "var(--crm-text-subtle)" }}>
                {it.n}
              </span>
              {it.label}
            </button>
          </li>
          {i < items.length - 1 && <span style={{ color: "var(--crm-text-subtle)" }}>·</span>}
        </React.Fragment>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
function StepUpload({ subject, setSubject, message, setMessage, pdfName, pdfSrc, onFileChange }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div>
          <label className="text-[11px] uppercase tracking-widest mb-1.5 block" style={{ color: "var(--crm-text-subtle)" }}>Subject</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. Beneficiary designation — Sarah Chen"
            data-testid="envelope-subject"
            className="w-full px-3 py-2.5 rounded-lg text-[14px] focus:outline-none"
            style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest mb-1.5 block" style={{ color: "var(--crm-text-subtle)" }}>Message to signers (optional)</label>
          <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="A short note that appears with the signing link…"
            data-testid="envelope-message"
            className="w-full px-3 py-2.5 rounded-lg text-[14px] focus:outline-none resize-none"
            style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }} />
        </div>
      </div>
      <label
        htmlFor="pdf-input"
        data-testid="pdf-drop"
        className="rounded-2xl p-10 text-center cursor-pointer transition"
        style={{
          background: pdfSrc ? "var(--crm-glass)" : "var(--crm-bg-elev)",
          border: `2px dashed ${pdfSrc ? "var(--crm-accent)" : "var(--crm-line)"}`,
        }}>
        <Upload size={28} className="mx-auto mb-3" style={{ color: pdfSrc ? "var(--crm-accent)" : "var(--crm-text-subtle)" }} />
        <div className="text-[14px]" style={{ color: "var(--crm-text)", fontWeight: 500 }}>
          {pdfSrc ? pdfName : "Drop or click to upload PDF"}
        </div>
        <div className="text-[11.5px] mt-1" style={{ color: "var(--crm-text-muted)" }}>
          {pdfSrc ? "Click to replace" : "Max 8 MB · PDF only"}
        </div>
        <input id="pdf-input" type="file" accept="application/pdf" onChange={onFileChange} className="hidden" />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
function StepSigners({ signers, setSigners }) {
  const [showPicker, setShowPicker] = useState(false);
  const addSigner = (base = {}) => {
    const color = SIGNER_COLORS[signers.length % SIGNER_COLORS.length];
    setSigners([...signers, {
      tempId: crypto.randomUUID(), name: "", email: "", role: "client", color, contact_id: null, ...base,
    }]);
  };
  const remove = (i) => setSigners(signers.filter((_, idx) => idx !== i));
  const update = (i, k, v) => setSigners(signers.map((s, idx) => idx === i ? { ...s, [k]: v } : s));

  return (
    <div className="space-y-4" data-testid="step-signers">
      <div className="flex flex-wrap gap-2">
        <button onClick={() => addSigner()} data-testid="add-manual-signer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px]"
          style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }}>
          <UserPlus size={13} /> Add signer manually
        </button>
        <button onClick={() => setShowPicker(true)} data-testid="add-from-crm-signer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px]"
          style={{ background: "var(--crm-accent-soft)", border: "1px solid var(--crm-line)", color: "var(--crm-accent)" }}>
          <Search size={13} /> Pick from CRM contacts
        </button>
      </div>

      {signers.length === 0 && (
        <div className="text-[13px] px-6 py-10 rounded-2xl text-center"
          style={{ background: "var(--crm-bg-elev)", border: "1px dashed var(--crm-line)", color: "var(--crm-text-subtle)" }}>
          No signers yet. Add at least one recipient. Signing order = list order.
        </div>
      )}

      {signers.map((s, i) => (
        <div key={s.tempId}
          className="grid grid-cols-1 md:grid-cols-[16px_1.2fr_1.5fr_120px_44px] gap-3 items-center px-4 py-3 rounded-xl"
          style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)" }}
          data-testid={`signer-row-${i}`}>
          <div className="w-3 h-3 rounded-full" style={{ background: s.color }} title={`Signer ${i + 1}`} />
          <input placeholder="Full name" value={s.name} onChange={(e) => update(i, "name", e.target.value)}
            data-testid={`signer-name-${i}`}
            className="px-3 py-2 rounded-lg text-[13px] focus:outline-none"
            style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }} />
          <input type="email" placeholder="Email" value={s.email} onChange={(e) => update(i, "email", e.target.value)}
            data-testid={`signer-email-${i}`}
            className="px-3 py-2 rounded-lg text-[13px] focus:outline-none"
            style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }} />
          <select value={s.role} onChange={(e) => update(i, "role", e.target.value)}
            data-testid={`signer-role-${i}`}
            className="px-3 py-2 rounded-lg text-[13px] focus:outline-none"
            style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }}>
            <option value="client">Client</option>
            <option value="advisor">Advisor</option>
            <option value="witness">Witness</option>
            <option value="cosigner">Co-signer</option>
          </select>
          <button onClick={() => remove(i)} data-testid={`remove-signer-${i}`}
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: "var(--crm-hover)", color: "var(--crm-text-muted)" }}>
            <X size={14} />
          </button>
        </div>
      ))}

      {showPicker && (
        <CrmContactPicker
          onPick={(c) => {
            addSigner({
              name: c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim(),
              email: c.email || "",
              contact_id: c.contact_id,
              role: "client",
            });
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

function CrmContactPicker({ onPick, onClose }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/crm/contacts", { params: { q: q || undefined, limit: 25 } })
        .then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6" onClick={onClose}
      style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)" }}
      data-testid="contact-picker">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl overflow-hidden"
        style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)", boxShadow: "0 30px 60px rgba(0,0,0,0.2)" }}>
        <div className="px-6 pt-6 pb-3 flex items-center gap-3">
          <Search size={16} style={{ color: "var(--crm-text-muted)" }} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search CRM contacts…"
            data-testid="picker-search"
            className="flex-1 bg-transparent focus:outline-none text-[14px]" style={{ color: "var(--crm-text)" }} />
          <button onClick={onClose} className="w-8 h-8 rounded-lg" style={{ color: "var(--crm-text-muted)" }}><X size={14} /></button>
        </div>
        <div className="max-h-[420px] overflow-y-auto" style={{ borderTop: "1px solid var(--crm-line-soft)" }}>
          {loading ? (
            <div className="p-8 text-center text-[13px]" style={{ color: "var(--crm-text-subtle)" }}>Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-[13px]" style={{ color: "var(--crm-text-subtle)" }}>No contacts match.</div>
          ) : items.map((c) => (
            <button key={c.id} onClick={() => onPick(c)} data-testid={`picker-item-${c.contact_id}`}
              className="w-full text-left px-6 py-3 flex items-center gap-3 transition"
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--crm-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              style={{ borderBottom: "1px solid var(--crm-line-soft)" }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px]"
                style={{ background: "var(--crm-accent-soft)", color: "var(--crm-accent)", fontWeight: 600 }}>
                {(c.first_name?.[0] || c.name?.[0] || "?").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] truncate" style={{ color: "var(--crm-text)" }}>{c.name}</div>
                <div className="text-[11.5px] truncate" style={{ color: "var(--crm-text-subtle)" }}>{c.email || c.phone || c.contact_id}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function StepFields({ pdfSrc, numPages, setNumPages, signers, fields, setFields }) {
  const [activeSigner, setActiveSigner] = useState(signers[0]?.tempId || null);
  const [activeKind, setActiveKind] = useState("signature");
  const [pageSize, setPageSize] = useState(null); // {width, height} of rendered page in px
  const pageRefs = useRef({});
  const [pageNum, setPageNum] = useState(1);

  useEffect(() => {
    if (!signers.find((s) => s.tempId === activeSigner)) setActiveSigner(signers[0]?.tempId || null);
  }, [signers, activeSigner]);

  const signer = signers.find((s) => s.tempId === activeSigner);

  const dropOnPage = (page, x, y) => {
    if (!signer) return;
    const kind = FIELD_KINDS.find((k) => k.type === activeKind);
    setFields([...fields, {
      tempId: crypto.randomUUID(),
      signer_tempId: signer.tempId,
      page, x_ratio: Math.max(0, Math.min(1 - kind.w, x)),
      y_ratio: Math.max(0, Math.min(1 - kind.h, y)),
      w_ratio: kind.w, h_ratio: kind.h, type: activeKind,
    }]);
  };

  const move = (id, dx, dy) => {
    setFields(fields.map((f) => f.tempId === id
      ? { ...f, x_ratio: Math.max(0, Math.min(1 - f.w_ratio, f.x_ratio + dx)), y_ratio: Math.max(0, Math.min(1 - f.h_ratio, f.y_ratio + dy)) }
      : f));
  };

  const removeField = (id) => setFields(fields.filter((f) => f.tempId !== id));

  return (
    <div className="flex gap-4 items-start" data-testid="step-fields">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 sticky top-4 space-y-4">
        {/* Signers */}
        <div className="rounded-2xl p-4" style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)" }}>
          <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--crm-text-subtle)" }}>Assign fields to</div>
          <div className="space-y-1">
            {signers.map((s) => (
              <button key={s.tempId} onClick={() => setActiveSigner(s.tempId)}
                data-testid={`assign-signer-${s.tempId}`}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-[12.5px] transition"
                style={{
                  background: activeSigner === s.tempId ? "var(--crm-hover)" : "transparent",
                  color: "var(--crm-text)",
                }}>
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                <span className="truncate">{s.name || `Signer ${signers.indexOf(s) + 1}`}</span>
              </button>
            ))}
          </div>
        </div>
        {/* Field kinds */}
        <div className="rounded-2xl p-4" style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)" }}>
          <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--crm-text-subtle)" }}>Field</div>
          <div className="grid grid-cols-2 gap-1.5">
            {FIELD_KINDS.map((k) => (
              <button key={k.type}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("kind", k.type)}
                onClick={() => setActiveKind(k.type)}
                data-testid={`field-kind-${k.type}`}
                className="flex items-center gap-1.5 px-2 py-2 rounded-lg text-[12px] transition cursor-grab"
                style={{
                  background: activeKind === k.type ? "var(--crm-accent-soft)" : "var(--crm-hover)",
                  color: activeKind === k.type ? "var(--crm-accent)" : "var(--crm-text)",
                  border: "1px solid var(--crm-line)",
                }}>
                <k.icon size={12} /> {k.label}
              </button>
            ))}
          </div>
          <div className="mt-3 text-[10.5px]" style={{ color: "var(--crm-text-subtle)" }}>
            Drag any kind onto the page, or click a kind then click on the page to drop.
          </div>
        </div>
        <div className="text-[11px]" style={{ color: "var(--crm-text-muted)" }}>
          {fields.length} field{fields.length === 1 ? "" : "s"} placed
        </div>
      </aside>

      {/* PDF stack */}
      <div className="flex-1 min-w-0 rounded-2xl p-4 space-y-3"
        style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)" }}>
        {!pdfSrc ? (
          <div className="text-[13px] p-10 text-center" style={{ color: "var(--crm-text-subtle)" }}>Upload a PDF first.</div>
        ) : (
          <Document file={pdfSrc} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
            {Array.from({ length: numPages }, (_, i) => i + 1).map((pn) => (
              <PdfPageWithFields
                key={pn} pageNum={pn} setPageNum={setPageNum}
                fields={fields.filter((f) => f.page === pn)}
                allFields={fields}
                signers={signers}
                signer={signer}
                kind={activeKind}
                onDrop={(x, y) => dropOnPage(pn, x, y)}
                onMove={move}
                onRemove={removeField}
              />
            ))}
          </Document>
        )}
      </div>
    </div>
  );
}

function PdfPageWithFields({ pageNum, fields, signers, signer, kind, onDrop, onMove, onRemove }) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  const handleClick = (e) => {
    if (!signer) return;
    const r = wrapRef.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    onDrop(x, y);
  };

  const handleDragOver = (e) => e.preventDefault();
  const handleDropEvt = (e) => {
    e.preventDefault();
    if (!signer) return;
    const r = wrapRef.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    onDrop(x, y);
  };

  return (
    <div className="relative mb-3 mx-auto shadow-lg" style={{ maxWidth: "100%", width: "fit-content" }}>
      <div className="text-[11px] mb-1" style={{ color: "var(--crm-text-subtle)" }}>Page {pageNum}</div>
      <div ref={wrapRef}
        className="relative cursor-crosshair"
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDrop={handleDropEvt}
        style={{ userSelect: "none" }}
        data-testid={`pdf-page-${pageNum}`}>
        <Page pageNumber={pageNum} width={720} renderAnnotationLayer={false} renderTextLayer={false}
          onRenderSuccess={(p) => setSize({ w: p.width, h: p.height })} />
        {fields.map((f) => {
          const sig = signers.find((s) => s.tempId === f.signer_tempId);
          return (
            <FieldChip key={f.tempId} f={f} sig={sig} parentRef={wrapRef}
              onMove={onMove} onRemove={onRemove}
            />
          );
        })}
      </div>
    </div>
  );
}

function FieldChip({ f, sig, parentRef, onMove, onRemove }) {
  const dragRef = useRef({ startX: 0, startY: 0, w: 0, h: 0 });
  const onMouseDown = (e) => {
    e.stopPropagation();
    if (!parentRef.current) return;
    const r = parentRef.current.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, w: r.width, h: r.height };
    document.body.style.userSelect = "none";
    const move = (ev) => {
      const dx = (ev.clientX - dragRef.current.startX) / dragRef.current.w;
      const dy = (ev.clientY - dragRef.current.startY) / dragRef.current.h;
      dragRef.current.startX = ev.clientX; dragRef.current.startY = ev.clientY;
      onMove(f.tempId, dx, dy);
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };
  return (
    <div
      onMouseDown={onMouseDown}
      data-testid={`field-chip-${f.tempId}`}
      className="absolute rounded-md flex items-center justify-center text-[10px] font-medium group cursor-move"
      style={{
        left: `${f.x_ratio * 100}%`, top: `${f.y_ratio * 100}%`,
        width: `${f.w_ratio * 100}%`, height: `${f.h_ratio * 100}%`,
        background: `${sig?.color || "#0071e3"}22`,
        border: `1.5px dashed ${sig?.color || "#0071e3"}`,
        color: sig?.color || "#0071e3",
      }}>
      <span className="pointer-events-none">{f.type}</span>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(f.tempId); }}
        className="absolute -top-2 -right-2 w-4 h-4 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
        style={{ background: sig?.color || "#0071e3", color: "#fff" }}
        data-testid={`remove-field-${f.tempId}`}>
        <X size={9} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
function StepReview({ subject, pdfName, signers, fields }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-3xl">
      <div className="rounded-2xl p-6" style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)" }}>
        <h3 className="text-[11px] uppercase tracking-widest mb-3" style={{ color: "var(--crm-text-subtle)" }}>Envelope</h3>
        <div className="text-[15px]" style={{ color: "var(--crm-text)" }}>{subject}</div>
        <div className="text-[12.5px] mt-1" style={{ color: "var(--crm-text-muted)" }}>{pdfName}</div>
      </div>
      <div className="rounded-2xl p-6" style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)" }}>
        <h3 className="text-[11px] uppercase tracking-widest mb-3" style={{ color: "var(--crm-text-subtle)" }}>Signers ({signers.length})</h3>
        <ol className="space-y-2">
          {signers.map((s, i) => (
            <li key={s.tempId} className="flex items-center gap-2 text-[13px]" style={{ color: "var(--crm-text)" }}>
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              <span className="font-medium">{s.name}</span>
              <span style={{ color: "var(--crm-text-muted)" }}>· {s.email} · {s.role}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className="rounded-2xl p-6 lg:col-span-2" style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)" }}>
        <h3 className="text-[11px] uppercase tracking-widest mb-3" style={{ color: "var(--crm-text-subtle)" }}>Fields ({fields.length})</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[12.5px]">
          {FIELD_KINDS.map((k) => {
            const n = fields.filter((f) => f.type === k.type).length;
            return (
              <div key={k.type} className="rounded-lg px-3 py-2" style={{ background: "var(--crm-bg-elev)", color: "var(--crm-text)" }}>
                {k.label}: <span className="font-medium">{n}</span>
              </div>
            );
          })}
        </div>
        <div className="mt-4 text-[11.5px]" style={{ color: "var(--crm-text-muted)" }}>
          Once sent, the document is hashed (SHA-256) and every event is chained. Each signer receives a private link. On the last signature, a cryptographically-sealed Certificate of Completion is generated automatically.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function arrayBufferToB64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}
